<template>
  <select-field
    v-if="filterType === TableHeader.SELECT_TYPE"
    :ref="v => (filterRef = v)"
    v-model="model"
    attach
    v-bind="inputProps"
    hide-details
    :data-testid="text"
    :menu-props="{ bottom: true, offsetY: true }"
    :items="items"
    :label="text"
    :clearable="!isMandatory"
    :prepend-inner-icon="icon"
  />

  <text-field
    v-else-if="filterType === TableHeader.STRING_TYPE"
    :ref="v => (filterRef = v)"
    v-model="model"
    data-lpignore="true"
    hide-details
    v-bind="inputProps"
    :data-testid="text"
    :clearable="!isMandatory"
    :label="text"
    :prepend-inner-icon="icon"
  />

  <generic-search
    v-else-if="filterType === TableHeader.GENERIC_SEARCH"
    :ref="v => (filterRef = v)"
    v-model="model"
    hide-details
    :data-testid="text"
    placeholder=""
    :prepend-inner-icon="icon"
    :clearable="!isMandatory"
    :label="text"
    v-bind="inputProps"
  />

  <cacheable-search-list
    v-else-if="filterType === TableHeader.CACHEABLE_SEARCH"
    :ref="v => (filterRef = v)"
    v-model="model"
    hide-details
    return-object
    placeholder=""
    :data-testid="text"
    :prepend-inner-icon="icon"
    :clearable="!isMandatory"
    :label="text"
    v-bind="inputProps"
  />

  <autocomplete-field
    v-else-if="filterType === TableHeader.AUTOCOMPLETE_TYPE"
    :ref="v => (filterRef = v)"
    v-model="model"
    attach
    hide-details
    :data-testid="text"
    :menu-props="{ bottom: true, offsetY: true }"
    :items="items"
    :label="text"
    :clearable="!isMandatory"
    :prepend-inner-icon="icon"
  />
  <div
    v-else-if="filterType === TableHeader.CHECKBOX_TYPE"
    class="checkbox-width"
  >
    <checkbox-field
      :ref="v => (filterRef = v)"
      v-model="model"
      class="mt-0"
      hide-details
      dense
      v-bind="inputProps"
      :data-testid="text"
      :label="text"
    />
  </div>

  <phone-input
    v-else-if="filterType === TableHeader.PHONE_TYPE"
    :ref="v => (filterRef = v)"
    v-model="model"
    data-lpignore="true"
    hide-details
    :data-testid="text"
    :clearable="!isMandatory"
    :label="text"
    :prepend-inner-icon="icon"
    require-full-length
  />

  <sitewide-search
    v-else-if="filterType === TableHeader.GLOBAL_SEARCH_FILTER_TYPE"
    :ref="v => (filterRef = v)"
    v-model="model"
    hide-details
    placeholder=""
    :data-testid="text"
    :clearable="!isMandatory"
    :label="text"
  />

  <!-- Range -->
  <date-filter-picker
    v-else-if="filterType === TableHeader.DATE_TYPE"
    :ref="v => (filterRef = v)"
    v-model="model"
    :data-testid="text"
    :color="color"
    :header-text="text"
    v-bind="inputProps"
  />

  <number-filter-picker
    v-else-if="filterType === TableHeader.NUMBER_RANGE"
    :ref="v => (filterRef = v)"
    v-model="model"
    :data-testid="text"
    :color="color"
    :label="text"
    v-bind="inputProps"
  />
</template>

<script setup>
import TableHeader from "@/classes/data-table/TableHeader";

import DateFilterPicker from "@/components/shared/DateFilterPicker.vue";
import NumberFilterPicker from "@/components/shared/NumberFilterPicker.vue";
import SitewideSearch from "@/components/shared/SitewideSearch.vue";
import PhoneInput from "@/components/shared/PhoneInput.vue";

import GenericSearch from "@/components/shared/GenericSearch.vue";
import CacheableSearchList from "@/components/shared/CacheableSearchList.vue";

import { ref, toRefs, watch } from "vue";

const props = defineProps({
  filterType: { type: String, required: true },
  items: { type: Array, required: false, default: () => [] },
  text: { type: String, required: true },
  isMandatory: Boolean,
  modelValue: {
    type: [String, Number, Object, Boolean, Array],
    required: false,
    default: null
  },
  icon: { type: [String, Object], required: false, default: null },
  color: {
    type: String,
    default: "section"
  },
  inputProps: {
    type: Object,
    required: false,
    default: null
  }
});

const emit = defineEmits(["update-filter", "update:model-value"]);

const {
  filterType,
  items,
  text,
  modelValue,
  isMandatory,
  icon,
  color,
  inputProps
} = toRefs(props);

const model = ref(null);

watch(
  modelValue,
  () => {
    if (valuesAreEqual(modelValue.value, model.value)) return;
    model.value = JSON.parse(JSON.stringify(modelValue.value));
  },
  { immediate: true, deep: true }
);

watch(
  model,
  () => {
    if (valuesAreEqual(model.value, modelValue.value)) return;
    emit("update:model-value", JSON.parse(JSON.stringify(model.value)));
    if (inputProps.value?.debounced) debounceAndUpdateFilter();
    else updateFilter();
  },
  { deep: true }
);

function updateFilter() {
  emit("update-filter");
}

let timeout;
function debounceAndUpdateFilter() {
  if (timeout) clearTimeout(timeout);
  timeout = setTimeout(() => updateFilter(), 500);
}

function valuesAreEqual(newV, oldV) {
  let current = newV;
  let previous = oldV;

  const isArray = Array.isArray(current) || Array.isArray(previous);
  const isObject = typeof current === "object" || typeof previous === "object";
  const requiresJSONCheck = isArray || isObject;

  if (requiresJSONCheck) {
    current = JSON.stringify(current);
    previous = JSON.stringify(previous);
  }

  return current === previous;
}

const filterRef = ref(null); // template ref

function focus() {
  if (filterRef.value?.focus) filterRef.value.focus();
  else filterRef.value.$el.querySelector("input").focus();
}

function clear() {
  if (filterRef.value?.reset) filterRef.value.reset();
  else model.value = null;
}

defineExpose({ focus, clear });
</script>
